import { createPromise, isForwardRef } from '@wix/thunderbolt-commons'
import React, { Suspense, useCallback, useEffect, useMemo, useRef } from 'react'
import type {
	ComponentModule,
	CreateSuspenseWrapperParams,
	WithDeferredHydrateOptionsCSR,
	WithDeferredHydrateWrapper,
} from './types'
import type { ILogger } from '@wix/thunderbolt-symbols'

export const EmptyDiv = ({
	logger,
	debugRendering,
	id,
	compClassType,
	height,
	shouldApplyId = false,
	onMount,
}: {
	logger?: ILogger
	debugRendering?: boolean
	id: any
	compClassType?: string
	height?: number
	shouldApplyId?: boolean
	onMount?: () => void
}) => {
	useEffect(() => {
		logger?.meter('react_render_error', {
			customParams: {
				compId: id,
				...(compClassType && { compClassType }),
				type: 'Suspense Fallback',
			},
		})
		const maybeCompClassType = compClassType ? ` - (${compClassType})` : ''
		console.error(`suspense rendered fallback for - ${id}${maybeCompClassType}`)
	}, [logger, debugRendering, id, compClassType])
	useEffect(() => {
		onMount?.()
	}, [onMount])
	return <div {...(shouldApplyId && { id })} {...(height && { style: { height: `${height}px` } })} />
}

function wrapPromise(promise: Promise<any>) {
	let status = 'pending'
	let response: any

	const suspender = promise.then(
		(res: any) => {
			status = 'success'
			response = res
		},
		(err: any) => {
			status = 'error'
			response = err
		}
	)

	const read = () => {
		switch (status) {
			case 'pending':
				throw suspender
			case 'error':
				throw response
			default:
				return response
		}
	}

	return { read, status }
}

function SuspenseInnerDeferred(props: any) {
	const ReactComponent = props.api.read()
	if (props.debugRendering) {
		console.log(`rendering { compId: ${props.id}}`)
	}
	return props.children(ReactComponent)
}
// called once per comp type when the component is loaded to the store
export const WithHydrateWrapperCSR: WithDeferredHydrateWrapper<WithDeferredHydrateOptionsCSR> = ({
	deferredComponentLoaderFactory,
	debugRendering,
	setIsWaitingSuspense,
	logger,
	placeholderHeight,
}) => {
	// called for each render
	const ViewportHydrator = (props: any, ref: any) => {
		const suspender = useMemo(() => {
			const { promise, resolver } = createPromise<React.ComponentType<any> | (() => React.ReactNode)>()
			const api = wrapPromise(promise)
			return { api, resolver }
		}, [])
		const pipeline = useRef<{ onUnmount?: Function; forceResolve?: () => void; disposed: boolean } | null>(null)

		useEffect(() => {
			setIsWaitingSuspense(props.id, true)
			const { componentPromise, onUnmount, forceResolve } = deferredComponentLoaderFactory!(props.id)
			const current = { onUnmount, forceResolve, disposed: false }
			pipeline.current = current
			componentPromise.then((...args) => {
				setIsWaitingSuspense(props.id, false)
				suspender.resolver(...args)
			})
			// The cleanup disposes the pipeline it created and clears the ref only if it still owns
			// it, so a replayed fallback effect (StrictMode runs effects twice in dev) cannot
			// mistake a disposed pipeline for a live one and falsely recover, and an older cleanup
			// cannot disconnect or clear a newer pipeline.
			return () => {
				current.disposed = true
				current.onUnmount?.()
				if (pipeline.current === current) {
					pipeline.current = null
				}
			}
		}, [props.id, suspender, suspender.resolver, props.children])

		// A live pipeline existing when the fallback mounts means hydration already started and
		// the boundary lost its SSR HTML (React discarded it and switched to client rendering,
		// e.g. after an update raced the hydration). The viewport observer may be watching the
		// discarded element and would never fire, so force the gate open - the load proceeds
		// through the regular componentPromise chain and suspender resolution.
		// On fresh client-side mounts the fallback's effect fires before the wrapper's, so no
		// pipeline exists yet and this is a no-op.
		const recoverAfterHtmlDiscard = useCallback(() => {
			const current = pipeline.current
			if (!current || current.disposed || !current.forceResolve) {
				return
			}
			console.log(`suspense recovering after SSR HTML discard, force resolving viewport gate - ${props.id}`)
			logger?.meter('suspense_recovered_after_html_discard', { customParams: { compId: props.id } })
			// Release the orphaned observer now rather than at wrapper unmount (which may be never):
			// while connected it holds a strong reference to the discarded element, pinning the
			// entire removed SSR subtree in memory. disconnect() is idempotent, so the wrapper's
			// unmount cleanup calling it again is harmless.
			current.onUnmount?.()
			current.forceResolve()
		}, [props.id])

		return (
			<Suspense
				fallback={
					<EmptyDiv
						height={placeholderHeight}
						logger={logger}
						debugRendering={debugRendering}
						id={props.id}
						shouldApplyId={true}
						onMount={recoverAfterHtmlDiscard}
					/>
				}
			>
				<SuspenseInnerDeferred api={suspender.api} debugRendering={debugRendering} id={props.id}>
					{(Comp: React.ComponentType<any>) => <Comp {...props} ref={isForwardRef(Comp) ? ref : null} />}
				</SuspenseInnerDeferred>
			</Suspense>
		)
	}

	return React.forwardRef(ViewportHydrator)
}

const LazyCompInner = ({
	setIsWaitingSuspense,
	...props
}: {
	setIsWaitingSuspense: CreateSuspenseWrapperParams['setIsWaitingSuspense']
	id: string
}) => {
	useEffect(() => {
		setIsWaitingSuspense(props.id, false)
	}, [props.id, setIsWaitingSuspense])
	return <></>
}

const createLazyComponent = ({ loadComponentModule }: { loadComponentModule: () => Promise<ComponentModule<any>> }) => {
	const idPromise = createPromise<string>()
	return {
		// React.lazy cannot be called within a component render, even if it is wrapped with useMemo it will be called on each render.
		// This is why we need to create the lazy component outside of the component render and pass the id when it's ready.
		LazyComp: React.lazy(() =>
			loadComponentModule().then(async (module) => {
				if (module.waitForLoadableReady) {
					const compId = await idPromise.promise
					await module.waitForLoadableReady(compId)
				}
				return { default: module.component }
			})
		),
		resolveId: idPromise.resolver,
	}
}

export const createSuspenseWrapper = ({
	loadComponentModule,
	setIsWaitingSuspense,
	getIsWaitingSuspense,
	logger,
	debugRendering,
}: CreateSuspenseWrapperParams) => {
	const { LazyComp, resolveId } = createLazyComponent({ loadComponentModule })
	return (props: any) => {
		resolveId(props.id)
		useEffect(() => {
			// In case the LazyComp was already loaded, LazyCompInner useEffect will run before
			if (getIsWaitingSuspense(props.id) === false) {
				return
			}
			setIsWaitingSuspense(props.id, true)
		}, [props.id])

		return (
			<Suspense
				fallback={
					<EmptyDiv id={props.id} compClassType={props.compClassType} logger={logger} debugRendering={debugRendering} />
				}
			>
				<LazyCompInner setIsWaitingSuspense={setIsWaitingSuspense} {...props} />
				<LazyComp {...props} />
			</Suspense>
		)
	}
}

export const createSsrSuspenseWrapper = async ({
	loadComponentModule,
	logger,
	debugRendering,
}: CreateSuspenseWrapperParams) => {
	const Comp = (await loadComponentModule()).component

	return (props: any) => (
		<Suspense
			fallback={
				<EmptyDiv id={props.id} compClassType={props.compClassType} logger={logger} debugRendering={debugRendering} />
			}
		>
			<Comp {...props} />
		</Suspense>
	)
}
